import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Table, Form, FormGroup, Input, Button, Label
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;

const defaultDrawModalContent = {
  success_draw_modal_en: "Thank you for participating, and we invite you to explore other levels to increase your chances in the monthly draw.\n\nGood luck! 🚀\n\n\nThe winner will be announced on The Royal Tank Museum’s official social media platforms, so be sure to follow us.",
  success_draw_modal_ar: "نشكر مشاركتك، وندعوك لاستكشاف مستويات أخرى وزيادة فرصك في القرعة الشهرية.\n\nحظاً موفقاً! 🚀\n\n\nسيتم الإعلان عن الفائز عبر منصات التواصل الاجتماعي الخاصة بـ متحف الدبابات الملكي، لذا لا تنسَ متابعتنا.",
};

const emptyLevel = {
  name: "",
  label_en: "",
  label_ar: "",
  icon_path: "",
  sort_order: 0,
  is_active: 1,
  points_per_question: 1,
  questions_count: 14,
  min_score: 0,
  ...defaultDrawModalContent,
};

const normalizeLevel = (level = {}, index = 0) => ({
  ...emptyLevel,
  ...level,
  sort_order: Number.isFinite(Number(level?.sort_order)) ? Number(level.sort_order) : index + 1,
  is_active: Number(level?.is_active ?? 1) === 0 ? 0 : 1,
  icon_path: String(level?.icon_path ?? level?.icon ?? "").trim(),
});

function GameLevels() {
  const [levels, setLevels] = useState([]);
  const [newLevel, setNewLevel] = useState({ ...emptyLevel });
  const [editLevel, setEditLevel] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);

  const notify = (setter, msg, t = 3500) => {
    setter(msg);
    setTimeout(() => setter(""), t);
  };

  const maxScoreFor = (lv) => {
    const ppq = Number(lv.points_per_question ?? 0);
    const qc = Number(lv.questions_count ?? 0);
    return ppq * qc;
  };

  const validateLevel = (lv) => {
    if (!Number.isFinite(lv.points_per_question) || lv.points_per_question <= 0) {
      return "Points per Question must be > 0.";
    }
    if (!Number.isFinite(lv.questions_count) || lv.questions_count <= 0) {
      return "Questions Count must be > 0.";
    }
    if (!Number.isFinite(lv.min_score) || lv.min_score < 0) {
      return "Min Score must be >= 0.";
    }
    const maxScore = maxScoreFor(lv);
    if (lv.min_score > maxScore) {
      return `Min Score cannot exceed max score (${maxScore}).`;
    }
    return "";
  };

  const fetchLevels = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/game/levels?all=1`, {
        headers: { "Accept-Language": "en" }
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setLevels(list.map((level, index) => normalizeLevel(level, index)));
    } catch {
      notify(setError, "Error fetching levels.");
    }
  }, []);

  useEffect(() => { fetchLevels(); }, [fetchLevels]);

  const submitNewLevel = async (e) => {
    e.preventDefault();
    if (!newLevel.name || !newLevel.label_en || !newLevel.label_ar) {
      notify(setError, "Name + EN/AR labels are required.");
      return;
    }
    const v = validateLevel(newLevel);
    if (v) {
      notify(setError, v);
      return;
    }
    try {
      const payload = {
        ...newLevel,
        icon: newLevel.icon_path || newLevel.icon || "",
      };
      await axios.post(`${BASE_URL}/game/levels`, payload);
      notify(setSuccess, "Level created.");
      setNewLevel({ ...emptyLevel });
      fetchLevels();
    } catch {
      notify(setError, "Failed to create level.");
    }
  };

  const submitEditLevel = async (e) => {
    e.preventDefault();
    if (!editLevel) return;
    const v = validateLevel(editLevel);
    if (v) {
      notify(setError, v);
      return;
    }
    try {
      const payload = {
        ...editLevel,
        icon: editLevel.icon_path || editLevel.icon || "",
      };
      await axios.put(`${BASE_URL}/game/levels/${editLevel.id}`, payload);
      notify(setSuccess, "Level updated.");
      setEditLevel(null);
      fetchLevels();
    } catch {
      notify(setError, "Failed to update level.");
    }
  };

  const deleteLevel = async (id) => {
    if (!window.confirm("Delete this level?")) return;
    try {
      await axios.delete(`${BASE_URL}/game/levels/${id}`);
      notify(setSuccess, "Level deleted.");
      fetchLevels();
    } catch {
      notify(setError, "Failed to delete level.");
    }
  };

  const moveLevel = (idx, dir) => {
    const next = [...levels];
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    const tmp = next[idx];
    next[idx] = next[swapIdx];
    next[swapIdx] = tmp;
    setLevels(next);
  };

  const saveOrder = async () => {
    try {
      const orderedIds = levels.map(l => l.id);
      await axios.put(`${BASE_URL}/game/levels/reorder`, { orderedIds });
      notify(setSuccess, "Order saved.");
      fetchLevels();
    } catch {
      notify(setError, "Failed to save order.");
    }
  };

  const resolveImageUrl = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return `${BASE_URL}/${s}`;
  };

  const uploadLevelIcon = async (file, onUploaded) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      const res = await axios.post(`${BASE_URL}/game/rules/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.success && res.data?.path) {
        onUploaded(res.data.path);
        return;
      }
      notify(setError, "Failed to upload level icon.");
    } catch {
      notify(setError, "Failed to upload level icon.");
    } finally {
      setUploading(false);
    }
  };

  const renderDrawModalEditors = (levelState, onFieldChange) => (
    <Card style={{ marginTop: 20 }}>
      <CardBody>
        <h6 style={{ marginBottom: 16 }}>Draw Modal Text</h6>
        <div className="text-muted" style={{ marginBottom: 16 }}>
          This is the only Success Page text managed from the backend for each level.
        </div>
        <Row form>
          <Col md="6">
            <FormGroup>
              <Label>Draw Modal Text (EN)</Label>
              <Input
                type="textarea"
                rows="8"
                value={levelState.success_draw_modal_en || ""}
                onChange={(e) => onFieldChange("success_draw_modal_en", e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Draw Modal Text (AR)</Label>
              <Input
                type="textarea"
                rows="8"
                value={levelState.success_draw_modal_ar || ""}
                onChange={(e) => onFieldChange("success_draw_modal_ar", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Game Levels</CardTitle>
            </CardHeader>
            <CardBody>
              {error && <div className="alert alert-danger">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <Row>
                <Col md="6">
                  <Form onSubmit={submitNewLevel}>
                    <h5>Add Level</h5>
                    <Row form>
                      <Col md="6">
                        <FormGroup>
                          <Label>Key (name)</Label>
                          <Input value={newLevel.name} onChange={(e) => setNewLevel({ ...newLevel, name: e.target.value })} />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Order</Label>
                          <Input type="number" value={newLevel.sort_order} onChange={(e) => setNewLevel({ ...newLevel, sort_order: Number(e.target.value) })} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row form>
                      <Col md="6">
                        <FormGroup>
                          <Label>Label (EN)</Label>
                          <Input value={newLevel.label_en} onChange={(e) => setNewLevel({ ...newLevel, label_en: e.target.value })} />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Label (AR)</Label>
                          <Input value={newLevel.label_ar} onChange={(e) => setNewLevel({ ...newLevel, label_ar: e.target.value })} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row form>
                      <Col md="3">
                        <FormGroup>
                          <Label>Points per Question</Label>
                          <Input type="number" value={newLevel.points_per_question} onChange={(e) => setNewLevel({ ...newLevel, points_per_question: Number(e.target.value) })} />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>Questions Count</Label>
                          <Input type="number" value={newLevel.questions_count} onChange={(e) => setNewLevel({ ...newLevel, questions_count: Number(e.target.value) })} />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>Min Score</Label>
                          <Input type="number" value={newLevel.min_score} onChange={(e) => setNewLevel({ ...newLevel, min_score: Number(e.target.value) })} />
                        </FormGroup>
                        <div className="text-muted" style={{ marginTop: "-8px", marginBottom: "12px" }}>
                          Max score: {maxScoreFor(newLevel) || 0}
                        </div>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label>Active</Label>
                          <Input type="select" value={newLevel.is_active} onChange={(e) => setNewLevel({ ...newLevel, is_active: Number(e.target.value) })}>
                            <option value={1}>Yes</option>
                            <option value={0}>No</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row form>
                      <Col md="12">
                        <FormGroup>
                          <Label>Level Icon</Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => uploadLevelIcon(e.target.files?.[0], (path) => setNewLevel((prev) => ({ ...prev, icon_path: path })))}
                            disabled={uploading}
                          />
                          {newLevel.icon_path ? (
                            <div style={{ marginTop: 8 }}>
                              <img src={resolveImageUrl(newLevel.icon_path)} alt="level-icon-new" style={{ maxHeight: 60, display: "block" }} />
                            </div>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                    {renderDrawModalEditors(newLevel, (fieldName, value) => setNewLevel({ ...newLevel, [fieldName]: value }))}
                    <Button color="primary" type="submit">Add</Button>
                  </Form>
                </Col>

                <Col md="6">
                  <Form onSubmit={submitEditLevel}>
                    <h5>Edit Level</h5>
                    {!editLevel && <div className="text-muted">Select a level from the list to edit.</div>}
                    {editLevel && (
                      <>
                        <Row form>
                          <Col md="6">
                            <FormGroup>
                              <Label>Key (name)</Label>
                              <Input value={editLevel.name} onChange={(e) => setEditLevel({ ...editLevel, name: e.target.value })} />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>Order</Label>
                              <Input type="number" value={editLevel.sort_order} onChange={(e) => setEditLevel({ ...editLevel, sort_order: Number(e.target.value) })} />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row form>
                          <Col md="6">
                            <FormGroup>
                              <Label>Label (EN)</Label>
                              <Input value={editLevel.label_en} onChange={(e) => setEditLevel({ ...editLevel, label_en: e.target.value })} />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>Label (AR)</Label>
                              <Input value={editLevel.label_ar} onChange={(e) => setEditLevel({ ...editLevel, label_ar: e.target.value })} />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row form>
                          <Col md="12">
                            <FormGroup>
                              <Label>Level Icon</Label>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => uploadLevelIcon(e.target.files?.[0], (path) => setEditLevel((prev) => ({ ...prev, icon_path: path })))}
                                disabled={uploading}
                              />
                              {editLevel.icon_path ? (
                                <div style={{ marginTop: 8 }}>
                                  <img src={resolveImageUrl(editLevel.icon_path)} alt="level-icon-edit" style={{ maxHeight: 60, display: "block" }} />
                                </div>
                              ) : null}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row form>
                          <Col md="3">
                            <FormGroup>
                              <Label>Points per Question</Label>
                              <Input type="number" value={editLevel.points_per_question} onChange={(e) => setEditLevel({ ...editLevel, points_per_question: Number(e.target.value) })} />
                            </FormGroup>
                          </Col>
                          <Col md="3">
                            <FormGroup>
                              <Label>Questions Count</Label>
                              <Input type="number" value={editLevel.questions_count} onChange={(e) => setEditLevel({ ...editLevel, questions_count: Number(e.target.value) })} />
                            </FormGroup>
                          </Col>
                          <Col md="3">
                            <FormGroup>
                              <Label>Min Score</Label>
                              <Input type="number" value={editLevel.min_score} onChange={(e) => setEditLevel({ ...editLevel, min_score: Number(e.target.value) })} />
                            </FormGroup>
                            <div className="text-muted" style={{ marginTop: "-8px", marginBottom: "12px" }}>
                              Max score: {maxScoreFor(editLevel) || 0}
                            </div>
                          </Col>
                          <Col md="3">
                            <FormGroup>
                              <Label>Active</Label>
                              <Input type="select" value={editLevel.is_active} onChange={(e) => setEditLevel({ ...editLevel, is_active: Number(e.target.value) })}>
                                <option value={1}>Yes</option>
                                <option value={0}>No</option>
                              </Input>
                            </FormGroup>
                          </Col>
                        </Row>
                        {renderDrawModalEditors(editLevel, (fieldName, value) => setEditLevel({ ...editLevel, [fieldName]: value }))}
                        <Button color="primary" type="submit">Save</Button>
                        <Button color="secondary" className="ml-2" onClick={() => setEditLevel(null)}>Cancel</Button>
                      </>
                    )}
                  </Form>
                </Col>
              </Row>

              <hr />

              <Row>
                <Col md="12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5>Levels Order</h5>
                    <Button color="info" onClick={saveOrder}>Save Order</Button>
                  </div>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Key</th>
                        <th>Label (EN)</th>
                        <th>Label (AR)</th>
                        <th>Level Icon</th>
                        <th>Points / Q</th>
                        <th>Questions Count</th>
                        <th>Min Score</th>
                        <th>Active</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {levels.map((lv, idx) => (
                        <tr key={lv.id}>
                          <td>{idx + 1}</td>
                          <td>{lv.name}</td>
                          <td>{lv.label_en}</td>
                          <td>{lv.label_ar}</td>
                          <td>
                            {lv.icon_path ? (
                              <img src={resolveImageUrl(lv.icon_path)} alt={`level-${idx + 1}`} style={{ maxHeight: 40 }} />
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{lv.points_per_question}</td>
                          <td>{lv.questions_count}</td>
                          <td>{lv.min_score}</td>
                          <td>{lv.is_active ? "Yes" : "No"}</td>
                          <td>
                            <Button size="sm" onClick={() => moveLevel(idx, "up")}>↑</Button>{" "}
                            <Button size="sm" onClick={() => moveLevel(idx, "down")}>↓</Button>{" "}
                            <Button size="sm" color="success" onClick={() => setEditLevel({ ...lv })}>Edit</Button>{" "}
                            <Button size="sm" color="danger" onClick={() => deleteLevel(lv.id)}>Delete</Button>
                          </td>
                        </tr>
                      ))}
                      {levels.length === 0 && (
                        <tr><td colSpan="10">No levels.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default GameLevels;
